import React, { useState, useEffect } from 'react';
import { WeeklyPlan } from '../types/weekly-plan';
import { Project } from '../types/project';
import { User } from '../types/user';
import { getWeeklyPlansByUser, createWeeklyPlan, deleteWeeklyPlan } from '../api/weekly-plans';
import { getProjects } from '../api/projects';
import { getAllUsers } from '../api/users';
import WeeklyPlanForm from '../components/WeeklyPlanForm';
import Button from '../components/Button';
import Card from '../components/Card';
import Select from '../components/Select';
import Badge from '../components/Badge';
import ProgressBar from '../components/ProgressBar';
import EmptyStateCard from '../components/EmptyStateCard';
import { getNonProductiveCategoryLabel } from '../constants/nonProductiveCategories';
import { CardSkeleton } from '../components/LoadingSkeleton';

const WeeklyPlannerPage: React.FC = () => {
  const [weeklyPlans, setWeeklyPlans] = useState<WeeklyPlan[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<WeeklyPlan | undefined>(undefined);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Load plans when user selection changes
  useEffect(() => {
    if (selectedUserId !== null) {
      loadWeeklyPlans(selectedUserId);
    }
  }, [selectedUserId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedProjects, fetchedUsers] = await Promise.all([
        getProjects(),
        getAllUsers(),
      ]);

      setProjects(fetchedProjects);
      setUsers(fetchedUsers);

      // Auto-select first user if available
      if (fetchedUsers.length > 0) {
        setSelectedUserId(fetchedUsers[0].id);
      }

      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyPlans = async (userId: string) => {
    try {
      const plans = await getWeeklyPlansByUser(userId);
      // Sort by week start date (newest first)
      plans.sort((a, b) => new Date(b.week_start_date).getTime() - new Date(a.week_start_date).getTime());
      setWeeklyPlans(plans);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load weekly plans';
      setError(errorMessage);
    }
  };

  const handleCreatePlan = async (planData: any) => {
    try {
      await createWeeklyPlan(planData);
      setShowForm(false);
      setEditingPlan(undefined);
      if (selectedUserId !== null) {
        await loadWeeklyPlans(selectedUserId);
      }
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create weekly plan';
      setError(errorMessage);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this weekly plan?')) {
      return;
    }

    try {
      await deleteWeeklyPlan(planId);
      if (selectedUserId !== null) {
        await loadWeeklyPlans(selectedUserId);
      }
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete weekly plan';
      setError(errorMessage);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getProjectName = (projectId: string): string => {
    if (!projects || !Array.isArray(projects)) return `Project #${projectId}`;
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : `Project #${projectId}`;
  };

  const getUserName = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.username) : `User #${userId}`;
  };

  const getWeekLabel = (plan: WeeklyPlan): string => {
    return `${formatDate(plan.week_start_date)} - ${formatDate(plan.week_end_date)}`;
  };

  const isCurrentWeek = (plan: WeeklyPlan): boolean => {
    const today = new Date();
    const weekStart = new Date(plan.week_start_date);
    const weekEnd = new Date(plan.week_end_date);
    return today >= weekStart && today <= weekEnd;
  };

  const isFutureWeek = (plan: WeeklyPlan): boolean => {
    const today = new Date();
    const weekStart = new Date(plan.week_start_date);
    return today < weekStart;
  };

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-10 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="mb-6">
          <div className="h-10 w-full max-w-md bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Weekly Planner</h1>
        <p className="text-gray-900 dark:text-gray-100">
          Plan your capacity allocation across projects for upcoming weeks
        </p>
      </div>

      {error && <div className="p-4 rounded-lg bg-error/10 dark:bg-error/20 border border-error/40 text-error">{error}</div>}

      {/* User Selection */}
      <Card variant="glass" className="mb-6">
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <Select
              label="Select User"
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name || user.username} ({user.email})
                </option>
              ))}
            </Select>
            <Button
              onClick={() => {
                setEditingPlan(undefined);
                setShowForm(true);
              }}
              variant="primary"
              disabled={selectedUserId === null || projects.length === 0}
            >
              + Create New Plan
            </Button>
          </div>
          {projects.length === 0 && (
            <div className="mt-4 p-4 rounded-lg bg-error/10 dark:bg-error/20 border border-error/40 text-error">
              No projects available. Please create a project first before planning.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Form */}
      {showForm && selectedUserId !== null && (
        <div className="mb-6">
          <WeeklyPlanForm
            plan={editingPlan}
            userId={selectedUserId}
            projects={projects}
            onSubmit={handleCreatePlan}
            onCancel={() => {
              setShowForm(false);
              setEditingPlan(undefined);
            }}
          />
        </div>
      )}

      {/* Weekly Plans List */}
      {selectedUserId !== null && (
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Plans for {getUserName(selectedUserId)}
          </h2>

          {weeklyPlans.length === 0 ? (
            <EmptyStateCard
              icon="🗓️"
              title="No weekly plans yet"
              message="Create your first plan to get started!"
            />
          ) : (
            <div className="flex flex-col gap-4">
              {weeklyPlans.map(plan => {
                const productiveHours = (plan.allocations ?? []).reduce((sum, a) => sum + a.hours, 0);
                const nonProductiveHours = (plan.non_productive_allocations || []).reduce((sum, a) => sum + a.hours, 0);
                const totalPlanned = productiveHours + nonProductiveHours;
                const FIXED_CAPACITY = 40;
                const percentageUsed = (totalPlanned / FIXED_CAPACITY) * 100;
                const currentWeek = isCurrentWeek(plan);
                const futureWeek = isFutureWeek(plan);

                return (
                  <Card
                    key={plan.id}
                    variant="glass"
                    className={currentWeek ? 'border-l-4 border-primary' : futureWeek ? 'border-l-4 border-success' : ''}
                  >
                    <Card.Body>
                      <div className="mb-4 pb-4 border-b-2 border-gray-100 dark:border-gray-700">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0 mb-2">
                              {getWeekLabel(plan)}
                              {currentWeek && (
                                <Badge variant="info" size="sm" className="ml-2">Current Week</Badge>
                              )}
                              {futureWeek && (
                                <Badge variant="success" size="sm" className="ml-2">Upcoming</Badge>
                              )}
                            </h3>
                            {plan.notes && (
                              <p className="text-gray-600 dark:text-gray-400 text-sm m-0">
                                {plan.notes}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleDeletePlan(plan.id)}
                            variant="danger"
                            size="sm"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>

                      <div className="text-gray-700 dark:text-gray-300">
                        {/* Capacity Summary */}
                        <div className="rounded-xl p-4 mb-4 bg-white/50 dark:bg-gray-900/40 border border-white/30 dark:border-gray-700/40">
                          <div className="flex justify-between mb-2">
                            <span className="font-medium">Weekly Capacity (40 hours)</span>
                            <span className="font-semibold">
                              {totalPlanned.toFixed(1)}h / 40h
                            </span>
                          </div>
                          <ProgressBar
                            percentage={percentageUsed}
                            color={percentageUsed > 100 ? 'danger' : percentageUsed > 90 ? 'warning' : 'success'}
                          />
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {percentageUsed.toFixed(0)}% allocated • {(40 - totalPlanned).toFixed(1)}h remaining
                          </div>
                        </div>

                        {/* Project Allocations */}
                        {(plan.allocations ?? []).length === 0 && (plan.non_productive_allocations || []).length === 0 ? (
                          <p className="text-gray-500 dark:text-gray-400 text-center p-4">
                            No allocations for this week
                          </p>
                        ) : (
                          <>
                            {(plan.allocations ?? []).map((allocation, idx) => (
                              <div
                                key={idx}
                                className="rounded-lg p-4 mb-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="font-semibold mb-2">
                                      {getProjectName(allocation.project_id)}
                                    </div>
                                    {allocation.notes && (
                                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        {allocation.notes}
                                      </div>
                                    )}
                                    {/* Task breakdown (if exists) */}
                                    {allocation.task_allocations && allocation.task_allocations.length > 0 && (
                                      <div className="mt-2 pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                                        {allocation.task_allocations.map((task, taskIdx) => (
                                          <div key={taskIdx} className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                            • {task.task_title ?? `Task #${task.task_id}`}: {task.hours.toFixed(1)}h
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <div className="text-lg font-bold text-primary">
                                      {allocation.hours.toFixed(1)}h
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      total
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Non-Productive Allocations */}
                            {plan.non_productive_allocations && plan.non_productive_allocations.length > 0 && (
                              <>
                                <div className="font-semibold text-sm text-gray-600 dark:text-gray-400 mt-6 mb-2">
                                  Non-Productive Time
                                </div>
                                {plan.non_productive_allocations.map((allocation, idx) => (
                                  <div
                                    key={idx}
                                    className="rounded-lg p-4 mb-2 bg-gray-100 dark:bg-gray-800/70 border border-gray-300 dark:border-gray-600"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="font-semibold mb-2">
                                          {getNonProductiveCategoryLabel(allocation.category_id)}
                                        </div>
                                        {allocation.notes && (
                                          <div className="text-sm text-gray-600 dark:text-gray-400">
                                            {allocation.notes}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold text-gray-700 dark:text-gray-300">
                                          {allocation.hours.toFixed(1)}h
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeeklyPlannerPage;
